/* BulkEditModal — reopens an applied import job in a side-by-side
 * editing experience:
 *   LEFT  · editable product table (same UX as ImportPreviewModal cells)
 *   RIGHT · collapsible panel with field-visibility toggles + LIVE
 *           buyer-view preview that updates as the seller flips toggles
 *
 * One Save button persists BOTH the product edits and the visibility
 * changes in parallel. Quote/Negotiate-friendly: SKU is locked so the
 * link to the catalog product is never broken. */
import { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import {
  Pencil, X, Save, Loader2, FileText, Settings2, Eye, EyeOff,
  PanelRightOpen, PanelRightClose,
} from "lucide-react";
import BuyerViewPreview from "./BuyerViewPreview";

const EDITABLE_FIELDS = [
  "sku", "name", "brand", "category", "description", "price", "retail_price",
  "moq", "stock", "case_size", "upc", "image", "fulfillment", "badge",
  "map_price", "tags",
];

const FIELD_LABELS = {
  sku:          "SKU code",
  name:         "Product name",
  brand:        "Brand",
  category:     "Category",
  description:  "Description",
  price:        "Wholesale price",
  retail_price: "Retail (MSRP)",
  moq:          "MOQ",
  stock:        "Stock on hand",
  case_size:    "Case size",
  upc:          "UPC / barcode",
  image:        "Image",
  fulfillment:  "Fulfillment",
  badge:        "Badges",
  map_price:    "MAP price",
  tags:         "Tags",
};

const FORCED_VISIBLE = new Set(["name", "price"]);

export default function BulkEditModal({ jobId, jobLabel, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);  // { sku, field } when active
  const [removed, setRemoved] = useState(new Set());
  const [dirty, setDirty] = useState({});         // { sku: { field: newValue } }
  const [busy, setBusy] = useState(false);

  // Visibility — loaded alongside the products so the right panel
  // shows the real current state for this seller.
  const [visibility, setVisibility] = useState({});
  const [visibilityDirty, setVisibilityDirty] = useState(false);

  // Right-panel toggle (default open — user explicitly asked for visibility
  // right alongside the edit). Collapses to give the table more room.
  const [panelOpen, setPanelOpen] = useState(true);

  // Sample product to drive the buyer-view preview. Pick the first row
  // (after the seller's edits flow in) so the preview feels real.
  const [previewSkuIdx, setPreviewSkuIdx] = useState(0);

  // ─────────────────────────── Load data ──────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [products, vis] = await Promise.all([
          api.get(`/seller/catalog-import/jobs/${jobId}/products`),
          api.get(`/seller/catalog-import/visibility`),
        ]);
        if (cancelled) return;
        setRows(products.data?.products || []);
        setVisibility(vis.data?.fields || {});
      } catch (e) {
        if (!cancelled) toast.error(e.response?.data?.detail || "Could not load this import");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [jobId]);

  // ────────────────────────── Editable columns ────────────────────
  const presentFields = useMemo(() => {
    const s = new Set();
    rows.forEach(r => EDITABLE_FIELDS.forEach(f => { if (r[f] !== null && r[f] !== undefined && r[f] !== "") s.add(f); }));
    ["sku", "name", "price"].forEach(f => s.add(f));
    return EDITABLE_FIELDS.filter(f => s.has(f));
  }, [rows]);

  const updateCell = (sku, field, raw) => {
    const v = coerceField(field, raw);
    setRows(prev => prev.map(r => r.sku === sku ? { ...r, [field]: v } : r));
    setDirty(prev => ({ ...prev, [sku]: { ...(prev[sku] || {}), [field]: v } }));
  };

  const removeRow = (sku) => {
    setRemoved(prev => { const n = new Set(prev); n.add(sku); return n; });
    setRows(prev => prev.filter(r => r.sku !== sku));
    // Reset preview index if we removed the previewed row
    setPreviewSkuIdx(idx => Math.max(0, Math.min(idx, rows.length - 2)));
  };

  // ────────────────────────── Visibility toggles ──────────────────
  const toggleVis = (k) => {
    if (FORCED_VISIBLE.has(k)) return;
    setVisibility(v => ({ ...v, [k]: !v[k] }));
    setVisibilityDirty(true);
  };

  // ────────────────────────── Save (parallel) ─────────────────────
  const save = async () => {
    setBusy(true);
    try {
      const ops = [];
      const productPatches = Object.entries(dirty)
        .filter(([sku]) => !removed.has(sku))
        .map(([sku, patches]) => ({ sku, ...patches }));
      if (productPatches.length > 0 || removed.size > 0) {
        ops.push(api.put(`/seller/catalog-import/jobs/${jobId}/products`, {
          products: productPatches, delete_skus: [...removed],
        }));
      }
      if (visibilityDirty) {
        const clean = { ...visibility };
        FORCED_VISIBLE.forEach(k => { clean[k] = true; });
        ops.push(api.put(`/seller/catalog-import/visibility`, { fields: clean }));
      }
      if (ops.length === 0) {
        toast.info("Nothing to save");
        return;
      }
      const results = await Promise.all(ops);
      const r = results[0]?.data || {};
      const parts = [];
      if (productPatches.length > 0) parts.push(`${r.updated || 0} updated`);
      if (removed.size > 0) parts.push(`${r.deleted || removed.size} removed`);
      if (visibilityDirty) parts.push("visibility saved");
      toast.success(parts.join(" · ") || "Saved");
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const dirtyCount = Object.keys(dirty).filter(s => !removed.has(s)).length;
  const totalPendingChanges = dirtyCount + removed.size + (visibilityDirty ? 1 : 0);
  const previewProduct = rows[previewSkuIdx] || rows[0];

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-7xl max-h-[94vh] overflow-hidden flex flex-col p-0" data-testid="bulk-edit-modal">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle className="display text-2xl tracking-tight inline-flex items-center gap-2">
            <FileText className="w-5 h-5 text-foreground/55"/>Edit imported products
          </DialogTitle>
          <DialogDescription className="text-foreground/55">
            Editing live catalog rows from <strong className="text-foreground">{jobLabel || "this import"}</strong>.
            Click any cell to change it, the × removes the product. Field visibility lives on the right — toggle to control what buyers see.
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 pb-3 flex items-center gap-2 flex-wrap text-[12px] text-foreground/65">
          <span className="chip bg-secondary text-[10px] uppercase mono">{rows.length} products</span>
          {dirtyCount > 0 && <span className="chip bg-amber-50 border border-amber-200 text-amber-800 text-[10px]">{dirtyCount} edited</span>}
          {removed.size > 0 && <span className="chip bg-rose-50 border border-rose-200 text-rose-700 text-[10px]">{removed.size} to remove</span>}
          {visibilityDirty && <span className="chip bg-indigo-50 border border-indigo-200 text-indigo-700 text-[10px]">visibility changed</span>}
          <div className="ml-auto inline-flex items-center gap-2">
            <button onClick={() => setPanelOpen(!panelOpen)}
              className="inline-flex items-center gap-1.5 text-[12px] text-foreground/70 hover:text-foreground"
              data-testid="toggle-vis-panel">
              {panelOpen ? <PanelRightClose className="w-3.5 h-3.5"/> : <PanelRightOpen className="w-3.5 h-3.5"/>}
              {panelOpen ? "Hide visibility panel" : "Show visibility panel"}
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-hidden flex">
          {/* LEFT — editable products table */}
          <div className="flex-1 overflow-hidden">
            {loading ? (
              <div className="h-full grid place-items-center text-foreground/45">
                <div className="text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto"/><div className="mt-3 text-sm">Loading products…</div></div>
              </div>
            ) : rows.length === 0 ? (
              <div className="h-full grid place-items-center text-foreground/45 px-6 text-center text-sm">
                No products from this import are currently in the catalog.
              </div>
            ) : (
              <div className="h-full overflow-auto px-6 pb-3 bg-secondary/30" data-testid="bulk-edit-scroll">
                <div className="text-[10.5px] text-foreground/55 mb-2 inline-flex items-center gap-1.5 pt-2">
                  <Pencil className="w-3 h-3"/>Click any cell to edit · scroll → to see all fields
                </div>
                <div className="rounded-xl border border-border bg-white overflow-x-auto">
                  <table className="text-[12px]" style={{ minWidth: Math.max(800, (presentFields.length + 1) * 150) + "px" }}>
                    <thead className="bg-secondary border-b border-border sticky top-0 z-10">
                      <tr>
                        <th className="w-9 px-2 py-2 sticky left-0 bg-secondary z-20"></th>
                        {presentFields.map(f => (
                          <th key={f} className="text-left px-3 py-2 text-[10px] uppercase tracking-wider mono text-foreground/55 whitespace-nowrap min-w-[150px]">
                            <span className="inline-flex items-center gap-1">
                              {f}
                              {/* Indicator showing if this field is currently visible to buyers */}
                              {!FORCED_VISIBLE.has(f) && (
                                visibility[f] === false
                                  ? <EyeOff className="w-2.5 h-2.5 text-foreground/30" title="Hidden from buyers"/>
                                  : <Eye className="w-2.5 h-2.5 text-emerald-600/70" title="Visible to buyers"/>
                              )}
                            </span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, idx) => (
                        <tr key={r.id || r.sku}
                          onMouseEnter={() => setPreviewSkuIdx(idx)}
                          className={`border-b last:border-b-0 border-border hover:bg-secondary/40 ${previewSkuIdx === idx ? "bg-secondary/25" : ""}`}
                          data-testid={`bulk-row-${r.sku}`}>
                          <td className="px-2 py-1.5 text-center sticky left-0 bg-white z-10">
                            <button onClick={() => removeRow(r.sku)}
                              className="w-5 h-5 rounded-md text-foreground/35 hover:text-rose-600 hover:bg-rose-50 grid place-items-center" title="Remove from catalog"
                              data-testid={`bulk-remove-${r.sku}`}>
                              <X className="w-3 h-3"/>
                            </button>
                          </td>
                          {presentFields.map(f => {
                            const active = editing && editing.sku === r.sku && editing.field === f;
                            const isDirtyCell = dirty[r.sku] && f in dirty[r.sku];
                            const value = r[f];
                            const locked = f === "sku";
                            return (
                              <td key={f}
                                className={`px-3 py-1.5 min-w-[150px] max-w-[260px] whitespace-nowrap overflow-hidden text-ellipsis ${locked ? "cursor-default" : "cursor-text"} ${active ? "bg-amber-50" : isDirtyCell ? "bg-amber-50/50" : ""}`}
                                onClick={() => !locked && setEditing({ sku: r.sku, field: f })}
                                data-testid={`bulk-cell-${r.sku}-${f}`}>
                                {active ? (
                                  <input
                                    autoFocus
                                    defaultValue={value === null || value === undefined ? "" : (Array.isArray(value) ? value.join(", ") : String(value))}
                                    onBlur={(e) => { updateCell(r.sku, f, e.target.value); setEditing(null); }}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") { updateCell(r.sku, f, e.target.value); setEditing(null); }
                                      if (e.key === "Escape") setEditing(null);
                                    }}
                                    className="w-full h-7 px-1.5 text-[12px] mono border border-amber-300 rounded bg-white focus:outline-none focus:border-amber-500"
                                    data-testid={`bulk-input-${r.sku}-${f}`}
                                  />
                                ) : renderCell(f, value)}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* RIGHT — visibility toggles + buyer preview */}
          {panelOpen && (
            <aside className="w-[340px] shrink-0 border-l border-border bg-white overflow-y-auto" data-testid="bulk-edit-right-panel">
              {/* Buyer preview at top — sticky so it stays in view while the seller scrolls the toggle list */}
              <div className="sticky top-0 z-10 bg-secondary/40 backdrop-blur px-4 pt-4 pb-3 border-b border-border">
                <div className="text-[10px] mono uppercase tracking-[0.22em] text-foreground/55 mb-2">Buyer view preview</div>
                {previewProduct ? (
                  <BuyerViewPreview product={previewProduct} visibility={visibility}/>
                ) : (
                  <div className="text-xs text-foreground/45 text-center py-6">No product to preview</div>
                )}
                <div className="text-[9.5px] mt-2 text-foreground/45 leading-relaxed">
                  Hover any row in the table → preview switches to that product.
                  {previewProduct && <> Currently previewing <span className="mono">{previewProduct.sku}</span>.</>}
                </div>
              </div>

              {/* Field visibility toggles */}
              <div className="px-4 pt-4 pb-6">
                <div className="text-[10px] mono uppercase tracking-[0.22em] text-foreground/55 mb-2 inline-flex items-center gap-1.5">
                  <Settings2 className="w-3 h-3"/>Field visibility
                </div>
                <p className="text-[10.5px] text-foreground/55 mb-3 leading-relaxed">
                  Toggle which fields buyers see. Required fields stay always-on.
                </p>
                <div className="space-y-1">
                  {Object.entries(FIELD_LABELS).map(([k, label]) => {
                    const on = !!visibility[k] || FORCED_VISIBLE.has(k);
                    const forced = FORCED_VISIBLE.has(k);
                    return (
                      <button key={k} onClick={() => toggleVis(k)} disabled={forced}
                        data-testid={`vis-${k}`}
                        className={`w-full text-left rounded-lg border p-2 flex items-center gap-2 transition-colors text-[12px] ${forced ? "border-border bg-secondary/40 cursor-default" : "border-border bg-white hover:border-foreground/40"}`}>
                        <span className={`shrink-0 w-8 h-4.5 rounded-full p-0.5 flex items-center transition-colors ${on ? "bg-foreground" : "bg-secondary border border-border"}`} style={{ width: 32, height: 18 }}>
                          <span className="w-3.5 h-3.5 rounded-full bg-white transition-transform" style={{ transform: on ? "translateX(13px)" : "translateX(0)" }}/>
                        </span>
                        <span className="flex-1 truncate">{label}</span>
                        {forced && <span className="text-[9px] mono text-foreground/40">REQ</span>}
                        <span className="text-foreground/35 shrink-0">{on ? <Eye className="w-3 h-3"/> : <EyeOff className="w-3 h-3"/>}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </aside>
          )}
        </div>

        <div className="border-t border-border bg-white px-6 py-4 flex items-center gap-3 flex-wrap">
          <Button variant="outline" onClick={onClose} className="rounded-full h-10 px-4">Close</Button>
          <div className="ml-auto inline-flex items-center gap-2">
            <Button onClick={save} disabled={busy || totalPendingChanges === 0} data-testid="bulk-save-btn"
              className="rounded-full h-10 px-5 bg-foreground text-background hover:bg-foreground/90">
              <Save className="w-3.5 h-3.5 mr-1.5"/>
              {busy ? "Saving…" : `Save${totalPendingChanges > 0 ? ` · ${totalPendingChanges} change${totalPendingChanges !== 1 ? "s" : ""}` : ""}`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function renderCell(field, v) {
  if (v === null || v === undefined || v === "") return <span className="text-foreground/30">—</span>;
  if (field === "price" || field === "retail_price" || field === "map_price") return <span className="mono">${Number(v).toFixed(2)}</span>;
  if (field === "moq" || field === "stock" || field === "case_size") return <span className="mono">{v}</span>;
  if (field === "image" && typeof v === "string" && v.startsWith("http")) return <a href={v} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="underline text-[11px]">link</a>;
  if (Array.isArray(v)) return <span className="text-[11px]">{v.slice(0, 3).join(", ")}</span>;
  return <span className="text-[12px]">{String(v)}</span>;
}

function coerceField(field, raw) {
  if (raw === null || raw === undefined) return null;
  const s = String(raw).trim();
  if (s === "") return null;
  if (["price", "retail_price", "map_price"].includes(field)) {
    const n = parseFloat(s.replace(/[$,]/g, ""));
    return Number.isFinite(n) ? n : null;
  }
  if (["moq", "stock", "case_size"].includes(field)) {
    const n = parseInt(s.replace(/[,]/g, ""), 10);
    return Number.isFinite(n) ? n : null;
  }
  if (field === "tags") {
    return s.split(/[,;|]/).map(t => t.trim()).filter(Boolean);
  }
  return s;
}
